import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Login from './components/Login/Login';
import Dashboard from './components/Dashboard/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import CanchaDemo from './components/Demo/CanchaDemo';
import LuminaDemo from './components/Demo/LuminaDemo';
import TenantPage from './components/TenantPage/TenantPage';
import CancelPage from './components/CancelPage/CancelPage';
import { supabase } from './supabaseClient';

const STEPS = [
  { n:"01", t:"Creás tu agenda", p:"Configurás servicios, horarios y días disponibles en minutos." },
  { n:"02", t:"Tus clientes reservan", p:"Compartís tu link y ellos eligen día y hora desde el celular, a cualquier hora." },
  { n:"03", t:"Vos te concentrás", p:"Recordatorios automáticos y notificaciones para que tu agenda siempre esté al día." },
];

const FEATURES = [
  { ic:"📅", t:"Agenda 24/7", p:"Tus clientes reservan cuando quieren, aunque sea a las 2 de la mañana. Sin perderte ningún turno." },
  { ic:"🔔", t:"Recordatorios automáticos", p:"WhatsApp y email automáticos para que nadie se olvide. Cero no-shows garantizado." },
  { ic:"📊", t:"Panel de control", p:"Todos tus turnos, cancelaciones y estadísticas en un solo lugar, desde cualquier dispositivo." },
  { ic:"⚡", t:"Múltiples empleados", p:"Calendarios independientes por profesional. Perfecto para equipos de trabajo." },
];

const PLANS = [
  { tier:"Individual", price:"$9.990", cycle:"por mes - ARS", hot:false,
    feats:["1 profesional","70 turnos / mes","Agenda online","Notificaciones email"], cta:"Empezar prueba gratis" },
  { tier:"Intermedio", price:"$29.990", cycle:"por mes · ARS", hot:true,
    feats:["Hasta 3 profesionales","Turnos ilimitados","Recordatorios email Y WhatsApp","Panel de estadísticas"], cta:"Empezar prueba gratis" },
  { tier:"Profesional", price:"$49.990", cycle:"por mes · ARS", hot:false,
    feats:["Profesionales ilimitados","Turnos ilimitados","Sucursales Ilimitadas","Recordatorios email Y WhatsApp"], cta:"Empezar prueba gratis" },
];

function LeadModal({ onClose, defaultPlan = "" }) {
  const [form, setForm] = useState({ nombre: "", email: "", negocio: "", plan: defaultPlan });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.negocio) { setError("Completá todos los campos."); return; }
    setLoading(true); setError("");
    const { error: dbErr } = await supabase.from("leads").insert([{
      nombre: form.nombre, email: form.email, negocio: form.negocio, plan: form.plan || null
    }]);
    setLoading(false);
    if (dbErr) { setError("Algo salió mal. Intentá de nuevo."); return; }
    setDone(true);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>✕</button>
        {done ? (
          <div className="modal-ok">
            <div className="modal-ok-icon">🎉</div>
            <h3>¡Listo! Ya recibimos tus datos.</h3>
            <p>Nos ponemos en contacto a la brevedad para contarte todo sobre TurnoKey.</p>
          </div>
        ) : (
          <>
            <div className="modal-title">¿Querés sumarte?</div>
            <div className="modal-sub">Dejanos tus datos y te contactamos para arrancar.</div>
            <form onSubmit={submit}>
              <div className="field">
                <label>TU NOMBRE</label>
                <input placeholder="Juan Pérez" value={form.nombre} onChange={set("nombre")} />
              </div>
              <div className="field">
                <label>EMAIL</label>
                <input type="email" placeholder="juan@example.com" value={form.email} onChange={set("email")} />
              </div>
              <div className="field">
                <label>NOMBRE DEL NEGOCIO</label>
                <input placeholder="Mi Barbería, Estudio X..." value={form.negocio} onChange={set("negocio")} />
              </div>
              <div className="field">
                <label>PLAN DE INTERÉS (opcional)</label>
                <select value={form.plan} onChange={set("plan")}>
                  <option value="">Sin preferencia</option>
                  <option value="Individual">Individual — $9.990/mes</option>
                  <option value="Intermedio">Intermedio — $29.990/mes</option>
                  <option value="Profesional">Profesional — $49.990/mes</option>
                </select>
              </div>
              {error && <p style={{ color:"#f87171", fontSize:"13px", marginBottom:"8px" }}>{error}</p>}
              <button className="modal-submit" type="submit" disabled={loading}>
                {loading ? "Enviando..." : "Enviar — me interesa"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function NavAuth({ theme, toggleTheme, onOpenModal }) {
  const { user, logout } = useAuth();
  const handleLogout = async () => { await logout(); };

  return (
    <div className="nav-right">
      <button className="theme-btn" onClick={toggleTheme} title="Cambiar tema">
        {theme === "dark" ? "☀️" : "🌙"}
      </button>
      {user ? (
        <>
          <Link to="/dashboard" className="btn-g">Mi panel</Link>
          <button className="btn-g" onClick={handleLogout}>Salir</button>
        </>
      ) : (
        <>
          <Link to="/login" className="btn-g">Iniciar sesión</Link>
          <button className="btn-p" onClick={onOpenModal}>Quiero sumarme</button>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('tk-theme') || 'dark');
  const [slot, setSlot]   = useState(null);
  const [modal, setModal] = useState({ open: false, plan: "" });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem('tk-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");
  const openModal   = (plan = "") => setModal({ open: true, plan });
  const closeModal  = () => setModal({ open: false, plan: "" });

  const slots = ["9:00","9:30","10:00","10:30","11:00","11:30"];
  const taken = ["9:30","10:30"];

  return (
    <AuthProvider>
      <Router>
        {modal.open && <LeadModal onClose={closeModal} defaultPlan={modal.plan} />}

        {/* NAV */}
        <nav className="nav">
          <Link to="/" className="logo">turno<span>key</span></Link>
          <div className="nav-center">
            <a href="#como-funciona">Cómo funciona</a>
            <a href="#features">Features</a>
            <a href="#precios">Precios</a>
          </div>
          <NavAuth theme={theme} toggleTheme={toggleTheme} onOpenModal={() => openModal()} />
        </nav>

        <Routes>
          <Route path="/login" element={<div className="app-container"><Login /></div>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/demo/los-manza" element={<CanchaDemo />} />
          <Route path="/demo/lumina"    element={<LuminaDemo />} />
          <Route path="/cancelar/:token" element={<CancelPage />} />
          <Route path="/:slug" element={<TenantPage />} />
          <Route path="/" element={
            <>
              <div className="demo-jump-group">
                <Link to="/demo/lumina"    className="demo-jump demo-jump-pro">Ver demo Profesional →</Link>
                <Link to="/demo/los-manza" className="demo-jump demo-jump-mid">Ver demo Intermedio →</Link>
              </div>

              {/* HERO */}
              <section style={{ borderBottom:"1px solid var(--border)" }}>
                <div className="hero">
                  <div>
                    <div className="pill"><div className="pill-dot"/>Reservas online para negocios</div>
                    <h1>Tus clientes reservan.<br/><em>Vos trabajás.</em></h1>
                    <p className="hero-sub">
                      TurnoKey le da a tu negocio una agenda online 24/7.
                      Sin llamadas, sin WhatsApps, sin idas y vueltas.
                    </p>
                    <div className="hero-ctas">
                      <button className="btn-p" onClick={() => openModal()}>Quiero sumarme</button>
                      <Link to="/demo/los-manza" className="btn-g">Demo Intermedio →</Link>
                      <Link to="/demo/lumina"    className="btn-g">Demo Profesional →</Link>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-head">
                      <div className="avatar">✂️</div>
                      <div>
                        <div className="biz-name">CStudio</div>
                        <div className="biz-sub">Lunes · 12 mayo</div>
                      </div>
                      <div className="badge-open">Abierto</div>
                    </div>
                    <div className="slots-lbl">HORARIOS DISPONIBLES</div>
                    <div className="slots">
                      {slots.map(s => (
                        <div key={s}
                          className={"slot"+(taken.includes(s)?" taken":slot===s?" sel":"")}
                          onClick={()=>!taken.includes(s)&&setSlot(s)}
                        >{s}</div>
                      ))}
                    </div>
                    <button className={`confirm-btn${slot?"":" idle"}`}>
                      {slot ? `Confirmar turno ${slot}` : "Elegí un horario"}
                    </button>
                    <div className="card-stats">
                      <div className="cs"><div className="cs-n">847</div><div className="cs-l">turnos / mes</div></div>
                      <div className="cs"><div className="cs-n">98%</div><div className="cs-l">satisfacción</div></div>
                      <div className="cs"><div className="cs-n">0</div><div className="cs-l">no-shows</div></div>
                    </div>
                  </div>
                </div>
              </section>

              {/* CÓMO FUNCIONA */}
              <div className="section" id="como-funciona">
                <div className="eyebrow">CÓMO FUNCIONA</div>
                <h2>Tres pasos y listo.</h2>
                <div className="steps">
                  {STEPS.map(({ n,t,p }) => (
                    <div className="step" key={n}>
                      <div className="step-n">{n}</div>
                      <h3>{t}</h3>
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="divider"/>

              {/* FEATURES */}
              <div className="section" id="features">
                <div className="eyebrow">FEATURES</div>
                <h2>Todo lo que tu negocio necesita.</h2>
                <div className="feats">
                  {FEATURES.map(({ ic,t,p }) => (
                    <div className="feat" key={t}>
                      <div className="feat-ic">{ic}</div>
                      <h3>{t}</h3>
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="divider"/>

              {/* PRICING */}
              <div className="section" id="precios">
                <div className="eyebrow">PRECIOS</div>
                <h2>Simple y transparente.</h2>
                <div className="plans">
                  {PLANS.map(({ tier,price,cycle,hot,feats }) => (
                    <div className={`plan${hot?" hot":""}`} key={tier}>
                      <div className="plan-tier">{tier.toUpperCase()}</div>
                      <div className="plan-price">{price}</div>
                      <div className="plan-cycle">{cycle}</div>
                      <ul className="plan-feats">
                        {feats.map(f => <li key={f}>{f}</li>)}
                      </ul>
                      <button className="btn-plan" onClick={() => openModal(tier)}>Me interesa</button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          } />
        </Routes>

        {/* FOOTER */}
        <footer className="footer">
          <div className="footer-logo">turno<span>key</span></div>
          <p>© 2025 TurnoKey · Hecho en Argentina</p>
        </footer>
      </Router>
    </AuthProvider>
  );
}
